import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Package, Trash2, Edit2, ImageIcon, Check, X, Search, AlertCircle, Eye, EyeOff, ChevronRight, ChevronLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// مكون فرعي لعرض الصور والتنقل بينها داخل الكرت
const ProductImageSlider = ({ mainImage, gallery, name }) => {
  // دمج الصورة الأساسية مع المعرض في مصفوفة واحدة
  const allImages = gallery && gallery.length > 0 ? gallery : [mainImage];
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextImage = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % allImages.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  if (!mainImage && allImages.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center text-gray-300">
        <ImageIcon size={40} />
      </div>
    );
  }

  return (
    <div className="relative w-full h-full group/slider">
      <img 
        src={allImages[currentIndex]} 
        alt={name} 
        className="w-full h-full object-cover transition-transform duration-500" 
      />
      
      {allImages.length > 1 && (
        <>
          {/* أسهم التنقل - تظهر عند الحوم فوق الصورة */}
          <button 
            onClick={prevImage}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 dark:bg-slate-900/80 p-1.5 rounded-full shadow-md opacity-0 group-hover/slider:opacity-100 transition-opacity z-10 text-gray-700 dark:text-white"
          >
            <ChevronRight size={18} />
          </button>
          <button 
            onClick={nextImage}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 dark:bg-slate-900/80 p-1.5 rounded-full shadow-md opacity-0 group-hover/slider:opacity-100 transition-opacity z-10 text-gray-700 dark:text-white"
          >
            <ChevronLeft size={18} />
          </button>

          {/* نقاط الترقيم في الأسفل */}
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-10">
            {allImages.map((_, idx) => (
              <div 
                key={idx} 
                className={`w-1.5 h-1.5 rounded-full transition-all ${idx === currentIndex ? 'bg-blue-600 w-3' : 'bg-white/50'}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const Inventory = ({ searchTerm }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showOutOfStock, setShowOutOfStock] = useState(false); 

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await axios.get('https://my-pharmacy-api.onrender.com/api/products');
      setProducts(res.data);
      setLoading(false);
    } catch (err) {
      console.error("خطأ في جلب البيانات:", err);
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(p => {
    const matchesSearch = 
      (p.name?.toLowerCase() || "").includes((searchTerm || "").toLowerCase()) ||
      (p.category?.toLowerCase() || "").includes((searchTerm || "").toLowerCase());
    
    const shouldShow = showOutOfStock ? true : p.stock > 0;
    
    return matchesSearch && shouldShow;
  });

  const handleDelete = async (id) => {
    if (window.confirm("⚠️ هل أنتِ متأكدة من حذف هذا الدواء نهائياً؟")) {
      try {
        await axios.delete(`https://my-pharmacy-api.onrender.com/api/products/${id}`);
        setProducts(products.filter(p => (p.id || p._id) !== id));
      } catch (err) {
        alert("حدث خطأ أثناء الحذف");
      }
    }
  };

  const startEdit = (p) => {
    setEditingId(p.id || p._id);
    setEditForm({ 
      name: p.name, 
      description: p.description || '', 
      price: p.price, 
      price_syp: p.price_syp, 
      stock: p.stock 
    });
  };

  const handleUpdate = async (id) => {
    try {
      await axios.put(`https://my-pharmacy-api.onrender.com/api/products/${id}`, editForm);
      setEditingId(null);
      fetchProducts(); 
    } catch (err) {
      alert("فشل تحديث البيانات");
    }
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center p-20 space-y-4">
      <div className="relative w-16 h-16">
        <div className="absolute top-0 left-0 w-full h-full border-4 border-blue-100 rounded-full"></div>
        <div className="absolute top-0 left-0 w-full h-full border-4 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
      </div>
      <p className="font-black text-gray-400 animate-pulse text-lg">جاري جرد المخزن...</p>
    </div>
  );

  return (
    <div dir="rtl" className="pb-24 px-2">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-4">
        <div>
          <h2 className="text-3xl font-black text-gray-900 dark:text-white">المخزون الحالي</h2>
          <p className="text-sm text-gray-400 font-bold mt-1">
            {filteredProducts.length} صنف متاح للعرض حالياً
          </p>
        </div>

        <button 
          onClick={() => setShowOutOfStock(!showOutOfStock)}
          className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-black text-xs transition-all shadow-sm ${
            showOutOfStock 
              ? 'bg-orange-500 text-white' 
              : 'bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-300 border border-gray-100 dark:border-slate-700'
          }`}
        >
          {showOutOfStock ? <Eye size={16}/> : <EyeOff size={16}/>}
          {showOutOfStock ? 'عرض المتوفر فقط' : 'إظهار الأدوية المنتهية'}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        <AnimatePresence mode='popLayout'>
          {filteredProducts.map((p) => (
            <motion.div 
              layout
              key={p.id || p._id} 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8, filter: 'blur(10px)' }}
              className={`bg-white dark:bg-slate-900 rounded-[40px] shadow-sm border flex flex-col group overflow-hidden transition-all hover:shadow-xl ${
                p.stock === 0 ? 'border-red-200 bg-red-50/30' : 'border-gray-100 dark:border-slate-800'
              }`}
            >
              
              <div className="relative h-52 w-full overflow-hidden bg-gray-50 dark:bg-slate-800">
                {/* تم استبدال الصورة العادية بالـ Slider الجديد */}
                <ProductImageSlider 
                  mainImage={p.image_url} 
                  gallery={p.gallery} 
                  name={p.name} 
                />
                
                {p.stock === 0 && (
                  <div className="absolute inset-0 bg-red-600/10 backdrop-blur-[1px] flex items-center justify-center z-20">
                    <span className="bg-red-600 text-white px-4 py-2 rounded-full font-black text-[10px] shadow-lg flex items-center gap-2">
                      <AlertCircle size={14} /> مخزون فارغ
                    </span>
                  </div>
                )}

                <div className="absolute top-4 left-4 z-20">
                  <div className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-md px-3 py-1.5 rounded-2xl shadow-sm border border-white/50 dark:border-slate-700">
                    <span className="text-lg font-black text-blue-600">{p.price}$</span>
                  </div>
                </div>
              </div>

              <div className="p-6 flex-1">
                <div className="mb-3">
                  <span className="text-[10px] font-black text-blue-500 bg-blue-50 dark:bg-blue-900/30 px-3 py-1 rounded-full mb-2 inline-block">
                    {p.category}
                  </span>
                  
                  {editingId === (p.id || p._id) ? (
                    <input 
                      type="text" 
                      className="w-full p-2 text-xl font-black border-b-2 border-blue-500 bg-transparent outline-none dark:text-white"
                      value={editForm.name}
                      onChange={e => setEditForm({...editForm, name: e.target.value})}
                    />
                  ) : (
                    <h3 className="font-black text-gray-800 dark:text-white text-xl truncate">{p.name}</h3>
                  )}
                </div>

                <div className="mb-6">
                  {editingId === (p.id || p._id) ? (
                    <textarea 
                      className="w-full p-2 text-sm bg-gray-50 dark:bg-slate-800 rounded-xl border-none resize-none font-bold dark:text-white"
                      value={editForm.description}
                      onChange={e => setEditForm({...editForm, description: e.target.value})}
                    />
                  ) : (
                    <p className="text-gray-400 dark:text-gray-500 text-xs font-bold leading-relaxed line-clamp-2">
                      {p.description || "لا يوجد وصف متوفر."}
                    </p>
                  )}
                </div>

                {editingId === (p.id || p._id) ? (
                  <div className="space-y-3 bg-blue-50 dark:bg-blue-900/10 p-4 rounded-3xl">
                    <div className="grid grid-cols-2 gap-2">
                      <input type="number" className="p-2 rounded-xl text-xs font-bold" value={editForm.price} onChange={e => setEditForm({...editForm, price: e.target.value})} placeholder="السعر $" />
                      <input type="number" className="p-2 rounded-xl text-xs font-bold" value={editForm.price_syp} onChange={e => setEditForm({...editForm, price_syp: e.target.value})} placeholder="السعر ل.س" />
                    </div>
                    <input type="number" className="w-full p-2 rounded-xl text-xs font-bold" value={editForm.stock} onChange={e => setEditForm({...editForm, stock: e.target.value})} placeholder="الكمية" />
                    <div className="flex gap-2">
                      <button onClick={() => handleUpdate(p.id || p._id)} className="flex-1 bg-blue-600 text-white p-2 rounded-xl font-black text-xs flex items-center justify-center gap-1"><Check size={14}/> حفظ</button>
                      <button onClick={() => setEditingId(null)} className="flex-1 bg-gray-200 text-gray-600 p-2 rounded-xl font-black text-xs flex items-center justify-center gap-1"><X size={14}/> إلغاء</button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`p-1.5 rounded-lg ${p.stock > 0 ? (p.stock > 5 ? 'bg-green-100 text-green-600' : 'bg-orange-100 text-orange-600') : 'bg-red-100 text-red-600'}`}>
                          <Package size={16} />
                        </div>
                        <span className={`font-black text-sm ${p.stock === 0 ? 'text-red-500' : 'text-gray-600 dark:text-gray-400'}`}>
                          {p.stock === 0 ? 'منتهي' : `${p.stock} قطعة`}
                        </span>
                      </div>
                      <span className="font-bold text-green-600 bg-green-50 dark:bg-green-900/20 px-3 py-1 rounded-lg text-sm">
                        {Number(p.price_syp).toLocaleString()} ل.س
                      </span>
                    </div>

                    <div className="flex gap-2 pt-2">
                      <button onClick={() => startEdit(p)} className="flex-1 bg-gray-900 dark:bg-blue-600 text-white py-3 rounded-2xl font-black text-xs flex items-center justify-center gap-2 hover:opacity-90">
                        <Edit2 size={14} /> تعديل
                      </button>
                      <button onClick={() => handleDelete(p.id || p._id)} className="p-3 bg-red-50 dark:bg-red-900/20 text-red-500 rounded-2xl hover:bg-red-500 hover:text-white transition-all">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {filteredProducts.length === 0 && (
        <div className="text-center py-20 bg-white dark:bg-slate-900 rounded-[40px] border-2 border-dashed border-gray-100 dark:border-slate-800">
          <Search size={48} className="mx-auto text-gray-200 mb-4" />
          <p className="text-gray-400 font-bold">كل الأدوية المطلوبة غير متوفرة حالياً.</p>
        </div>
      )}
    </div>
  );
};

export default Inventory;